import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { ArrowRight, Calendar, Home, Users, Award, Shield, Phone, Mail, MapPin as MapPinIcon, Facebook, Twitter, Linkedin, Instagram, ChevronLeft, ChevronRight, Newspaper } from 'lucide-react';
import { useHomeData, useProjets, useContactInfo, useSocialLinks, useGlobalStatistiques, useHeroSliders, useActualites } from '../hooks/usePublicApi';
import { useTexts, useSiteInfo } from '../hooks/useTexts';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ErrorMessage from '../components/common/ErrorMessage';
import ProjectCard from '../components/common/ProjectCard';

export default function HomePage() {
  // Récupération des données dynamiques
  const { data: homeData, loading: homeLoading, error: homeError } = useHomeData();
  const { data: projets, loading: projetsLoading, error: projetsError } = useProjets();
  const { data: contactInfo } = useContactInfo();
  const { data: socialLinks } = useSocialLinks();
  const { data: globalStats, loading: statsLoading } = useGlobalStatistiques();
  const { data: heroSliders, loading: heroSlidersLoading } = useHeroSliders();
  
  // Hook pour les textes dynamiques
  const { getText } = useTexts();
  const { getSiteInfo } = useSiteInfo();
  
  // État pour le slider hero
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  
  // Images du slider hero depuis la base de données
  const heroImages = heroSliders || [];
  
  // Auto-play du slider toutes les 5 secondes (pause si le curseur est sur la zone de texte)
  useEffect(() => {
    if (heroImages.length <= 1 || isPaused) return;
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroImages.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [heroImages.length, isPaused]);

  // Données par défaut en cas de chargement ou d'erreur
  const featuredProjects = (projets || []).slice(0, 3);
  const latestUpdates: any[] = []; // TODO: Implémenter les mises à jour de projet

  const { data: newsData, loading: newsLoading } = useActualites({
    afficher_accueil: true,
    ordering: '-date_publication',
    page: 1,
  });

  const highlightedNews = (newsData || []).slice(0, 3);

  const resolveImageUrl = (url?: string | null) => {
    if (!url) return null;
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    const base = import.meta.env.VITE_API_BASE_URL || '';
    const normalizedBase = base.endsWith('/api') ? base.replace(/\/api$/, '') : base;
    return `${normalizedBase}${url}`;
  };

  // Récupération des informations spécifiques
  const homeInfo = homeData.home || [];
  const contactData = contactInfo || [];
  const socialData = socialLinks || [];

  // Fonction pour récupérer une valeur par clé
  const getInfoByKey = (infos: any[], key: string) => {
    return infos.find(info => info.cle_info === key)?.valeur || infos.find(info => info.cle_info === key)?.contenu || '';
  };

  // Données de contact
  const phone = getInfoByKey(contactData, 'contact_telephone') || '';
  const email = getInfoByKey(contactData, 'contact_email') || '';
  const address = getInfoByKey(contactData, 'contact_adresse') || '';

  // Liens sociaux
  const facebookUrl = getInfoByKey(socialData, 'social_facebook') || '';
  const twitterUrl = getInfoByKey(socialData, 'social_twitter') || '';
  const linkedinUrl = getInfoByKey(socialData, 'social_linkedin') || '';
  const instagramUrl = getInfoByKey(socialData, 'social_instagram') || '';

  // Gestion des erreurs
  if (homeError || projetsError) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <ErrorMessage 
          error={homeError || projetsError || 'Erreur lors du chargement des données'} 
          onDismiss={() => window.location.reload()} 
        />
      </div>
    );
  }

  // Affichage du loading
  if (homeLoading || projetsLoading || statsLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  // Données du hero section
  const currentHeroImage = heroImages.length > 0 ? heroImages[currentSlide] : null;
  const shouldShowTitle = currentHeroImage?.afficher_titre !== false; // Afficher par défaut si non défini
  
  // Si le titre du slide doit s'afficher, utiliser le titre du slide, sinon utiliser le titre principal
  const heroTitle = shouldShowTitle 
    ? currentHeroImage?.titre 
    : (getInfoByKey(homeInfo, 'home_titre_principal') || getText('home.hero.title'));
  
  // Sous-titre : utiliser le sous-titre principal si pas de slide spécifique
  const heroSubtitle = shouldShowTitle 
    ? null  // Pas de sous-titre dans le titre si on utilise un slide spécifique
    : (getInfoByKey(homeInfo, 'home_sous_titre') || getText('home.hero.subtitle'));
  
  // Le texte du bas (description) doit être spécifique à chaque slide si disponible
  // Sinon utiliser la description générale
  const heroDescription = shouldShowTitle && currentHeroImage?.description
    ? currentHeroImage.description  // Utiliser la description du slide actuel pour le texte du bas
    : (getInfoByKey(homeInfo, 'home_description') || getText('home.hero.description'));
  
  const heroImageUrl = currentHeroImage?.image_url_final || 'https://images.pexels.com/photos/1396132/pexels-photo-1396132.jpeg?auto=compress&cs=tinysrgb&w=1920';
  
  // Fonctions pour le slider
  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % (heroImages.length || 1));
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + (heroImages.length || 1)) % (heroImages.length || 1));
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        {/* Images du slider */}
        {heroImages.length > 0 ? (
          heroImages.map((image, index) => (
            <div
              key={image.id}
              className={`absolute inset-0 bg-cover bg-center bg-no-repeat transition-opacity duration-1000 ${
                index === currentSlide ? 'opacity-100' : 'opacity-0'
              }`}
              style={{
                backgroundImage: image.image_url_final ? `url(${image.image_url_final})` : undefined
              }}
            />
          ))
        ) : (
          <div 
            className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-100"
            style={{
              backgroundImage: 'url(https://images.pexels.com/photos/1396132/pexels-photo-1396132.jpeg?auto=compress&cs=tinysrgb&w=1920)'
            }}
          />
        )}
        
        {/* Contenu */}
        <div 
          className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
        >
          {/* Overlay gradient uniquement sur la zone de texte */}
          <div className="absolute inset-0 -mx-4 sm:-mx-6 lg:-mx-8 -my-8 bg-gradient-to-br from-blue-900/20 to-blue-700/20 rounded-lg -z-10" />
          <div className="relative z-10">
          {heroTitle && (
            <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
              {heroTitle}
              {heroSubtitle && (
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-teal-400">
                  {heroSubtitle}
                </span>
              )}
            </h1>
          )}
          {heroDescription && (
            <p className="text-xl md:text-2xl mb-8 text-blue-100 max-w-3xl mx-auto leading-relaxed">
              {heroDescription}
            </p>
          )}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/projects"
              className="inline-flex items-center px-8 py-4 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-all duration-300 transform hover:scale-105 shadow-lg"
            >
              {getText('home.hero.discover_projects')}
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center px-8 py-4 bg-transparent border-2 border-white text-white font-semibold rounded-lg hover:bg-white hover:text-blue-900 transition-all duration-300"
            >
              {getText('home.hero.contact_us')}
            </Link>
          </div>
          </div>
        </div>
        
        {/* Contrôles du slider */}
        {heroImages.length > 1 && (
          <>
            {/* Boutons précédent/suivant */}
            <button
              onClick={prevSlide}
              className="absolute left-4 top-1/2 -translate-y-1/2 z-20 p-3 bg-black/30 hover:bg-black/50 text-white rounded-full transition-all"
              aria-label="Slide précédent"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <button
              onClick={nextSlide}
              className="absolute right-4 top-1/2 -translate-y-1/2 z-20 p-3 bg-black/30 hover:bg-black/50 text-white rounded-full transition-all"
              aria-label="Slide suivant"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
            
            {/* Indicateurs de pagination */}
            <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex gap-2">
              {heroImages.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentSlide(index)}
                  className={`h-2 rounded-full transition-all ${
                    index === currentSlide ? 'w-8 bg-white' : 'w-2 bg-white/50 hover:bg-white/75'
                  }`}
                  aria-label={`Aller au slide ${index + 1}`}
                />
              ))}
            </div>
          </>
        )}
      </section>

      {/* Stats Section */}
      {/* <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center group">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4 group-hover:bg-blue-200 transition-colors">
                  <stat.icon className="w-8 h-8 text-blue-600" />
                </div>
                <div className="text-3xl font-bold text-gray-900 mb-2">{stat.value}</div>
                <div className="text-gray-600">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section> */}

      {/* Featured Projects */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {getText('home.projects.title')}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {getText('home.projects.description')}
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-8 mb-12">
            {featuredProjects.map((project) => (
              <ProjectCard key={project.id} project={project} className="w-full max-w-sm md:w-auto md:flex-shrink-0" />
            ))}
          </div>

            {/* Afficher si projets plus que 3 */}
            {projets.length > 3 && (
              <div className="text-center">
                <Link
                  to="/projects"
                  className="inline-flex items-center px-8 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors"
                >
                  {getText('home.projects.see_all')}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
              </div>
            )}

        </div>
      </section>

      {/* News Section */}
      {newsLoading ? (
        <div className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-center">
            <LoadingSpinner />
          </div>
        </div>
      ) : highlightedNews.length > 0 ? (
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-12 gap-6">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
                  {(() => {
                    const title = getText('home.news.title');
                    return title === 'home.news.title' ? 'Nos actualités' : title;
                  })()}
                </h2>
                <p className="text-lg text-gray-600 max-w-2xl">
                  {(() => {
                    const subtitle = getText('home.news.subtitle');
                    return subtitle === 'home.news.subtitle'
                      ? 'Restez informés des dernières annonces, événements et mises à jour de nos projets.'
                      : subtitle;
                  })()}
                </p>
              </div>
              <Link
                to="/news"
                className="inline-flex items-center px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors"
              >
                {(() => {
                  const label = getText('home.news.cta');
                  return label === 'home.news.cta' ? 'Voir toutes les actualités' : label;
                })()}
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </div>

            <div className="grid gap-8 md:grid-cols-3">
              {highlightedNews.map((item) => {
                const imageUrl = resolveImageUrl(item.image_url_final);
                const excerpt = item.resume?.trim() || item.contenu.slice(0, 180);
                return (
                  <article
                    key={item.id}
                    className="bg-white rounded-2xl shadow-sm border border-gray-100 hover:border-blue-200 transition overflow-hidden"
                  >
                    {imageUrl && (
                      <Link to={`/news/${item.slug}`}>
                        <img
                          src={imageUrl}
                          alt={item.titre}
                          className="w-full h-48 object-cover"
                          loading="lazy"
                        />
                      </Link>
                    )}
                    <div className="p-6">
                      <div className="flex items-center text-sm text-gray-500 mb-3">
                        <Calendar className="w-4 h-4 mr-2" />
                        {new Intl.DateTimeFormat('fr-FR', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric',
                        }).format(new Date(item.date_publication))}
                      </div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-3 line-clamp-2">
                        <Link to={`/news/${item.slug}`} className="hover:text-blue-600 transition-colors">
                          {item.titre}
                        </Link>
                      </h3>
                      <p className="text-gray-600 leading-relaxed line-clamp-3">
                        {excerpt}
                        {item.contenu.length > excerpt.length && '...'}
                      </p>
                      <Link
                        to={`/news/${item.slug}`}
                        className="mt-6 inline-flex items-center text-sm font-semibold text-blue-600 hover:text-blue-700 transition-colors"
                      >
                        <Newspaper className="w-4 h-4 mr-2" />
                        {(() => {
                          const label = getText('home.news.read_more');
                          return label === 'home.news.read_more' ? 'Lire l’article' : label;
                        })()}
                      </Link>
                    </div>
                  </article>
                );
              })}
            </div>
          </div>
        </section>
      ) : null}

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-blue-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            {getText('home.cta.title')}
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            {getText('home.cta.description')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="inline-flex items-center px-8 py-4 bg-white text-blue-600 font-semibold rounded-lg hover:bg-gray-100 transition-colors"
            >
              {getText('home.cta.contact_us')}
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center px-8 py-4 bg-transparent border-2 border-white text-white font-semibold rounded-lg hover:bg-white hover:text-blue-600 transition-colors"
            >
              {getText('home.cta.ask_advice')}
            </Link>
          </div>
        </div>
      </section>      
    </div>
  );
}
